// Durum zaman çizelgesi — ApexCharts `rangeBar` (yatay timeline).
//
// Kullanıcı isteği: "durum geçişlerini grafik üzerinde zamana göre görmek,
// kullanıcı istediği durumu legend'ından açıp kapatabilsin". Her durum kendi
// SATIRINI alıyor; o durumun sürdüğü zaman aralıkları bant olarak çiziliyor.
//
// Bu dosyadaki her seçenek ölçülerek kondu (ApexCharts 6.10 sunucu tarafında
// çizdirilip piksel/eksen çıktısı okundu). Aşağıdaki dördü ATLANIRSA ekran
// sessizce bozuluyor:
//
//  1. `rangeBarGroupRows: true` — YOKSA bant yüksekliği satırın 1/3'üne
//     düşüyor (52,7 px → 17,6 px) ve satırlar kayıyor.
//  2. `legend.showForSingleSeries: true` — YOKSA tek durumlu etikette legend
//     HİÇ çizilmiyor. En sık senaryolardan biri bu ("1 saat" boyunca hep
//     ACTIVE) ve kullanıcının asıl istediği özellik orada yok oluyor.
//  3. `xaxis.labels.formatter` VERİLMİYOR — vermek TimeScale'i tamamen
//     kapatıyor: 9 temiz yarım saat tiki yerine 80 dakikalık, hiçbir saat
//     sınırına oturmayan 4 tik çıkıyor. Yerel saat `datetimeUTC: false` ile
//     zaten sağlanıyor (tarayıcı dilimi = Europe/Istanbul).
//  4. `xaxis.min/max` SAYI olarak veriliyor — Date/ISO verilirse Range.js
//     `typeof === 'number'` kontrolüne takılıp SESSİZCE yok sayılıyor.
//
// Legend açma/kapama ApexCharts'ın KENDİ mekanizmasına bırakıldı (React state
// tutulmuyor). Elle gizleme denenirse iki ayrı gerçek kaynak oluşuyor:
// kütüphane aynı tıklamada `collapsedSeries`e de yazıyor ve sonraki tıklamada
// React'ın değil onun durumu okunuyor. Eksen `xaxis.min/max` ile sabitlendiği
// için, kullanıcı tüm satırları kapatsa bile eksen bozulmuyor.

import { lazy, Suspense, useMemo } from 'react'
import type { ApexOptions } from 'apexcharts'
import { tr, trTarihSaat } from '../../lib/trFormat'
import type { DurumCizelgesi as Cizelge } from './api'
import { YEREL } from './grafikYerel'

const Chart = lazy(() => import('react-apexcharts'))

const YAZI = 'Montserrat, system-ui, sans-serif'
const MONO = '"IBM Plex Mono", monospace'

const RENK = {
  izgara: '#ECF0F6', // --line-soft
  govde: '#5A6B80', // --muted
} as const

// Durum → renk KARARLI olmalı: aynı durum her aralıkta, her etikette aynı
// rengi almalı, yoksa kullanıcı renk hafızası kuramaz. Bilinen durumlar elle
// eşleniyor; geri kalanlar ada göre deterministik olarak paletten seçiliyor.
// (ApexCharts renkleri doğrudan SVG özniteliğine bastığı için CSS değişkeni
// çözülmez — literal yazılmak zorunda.)
const BILINEN: Record<string, string> = {
  ACTIVE: '#2E7D46', // yeşil — çalışıyor
  RUNNING: '#2E7D46',
  ON: '#2E7D46',
  PASSIVE: '#8C97A8', // gri — duruyor
  STOPPED: '#8C97A8',
  OFF: '#8C97A8',
  FAULT: '#C0392B', // kırmızı — arıza
  ALARM: '#C0392B',
  ERROR: '#C0392B',
  UNKNOWN: '#C79A2E', // amber — bilinmiyor
}

const PALET = ['#17427E', '#7B4FA8', '#1F7A8C', '#B5651D', '#5A6B80', '#4A7C2F']

function renkSec(durum: string, sira: number): string {
  return BILINEN[durum.trim().toUpperCase()] ?? PALET[sira % PALET.length]
}

/** Süreyi insan ölçüsüne çevirir: 45 sn · 12 dk · 3,2 sa · 4,1 gün. */
function sure(saniye: number): string {
  if (saniye < 60) return `${tr(saniye, 0)} sn`
  if (saniye < 3600) return `${tr(saniye / 60, 0)} dk`
  if (saniye < 86400) return `${tr(saniye / 3600, 1)} sa`
  return `${tr(saniye / 86400, 1)} gün`
}

/** İpucu innerHTML ile basılıyor — durum metni ajandan geliyor, kaçırılmalı. */
function kacir(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  )
}

export default function DurumCizelgesi({
  cizelge,
  zoomVar,
  zoomIste,
  zoomSifirla,
}: {
  cizelge: Cizelge
  zoomVar: boolean
  zoomIste: (bas: number, son: number) => void
  zoomSifirla: () => void
}) {
  const { bantlar, karne, mod } = cizelge
  const kovaMi = mod === 'kova'

  // Satır sırası karneden geliyor (toplam süreye göre azalan) — grafik ile
  // altbilgi aynı sırayı göstersin diye.
  const durumlar = useMemo(() => karne.map((k) => k.durum), [karne])

  // useMemo ZORUNLU: react-apexcharts derin karşılaştırması fonksiyon taşıyan
  // seçenek nesnesini daima "değişti" sayıyor (typeof !== 'object' → false) ve
  // her render'da updateOptions çağırıyor. O da eksen sınırlarını geri alıyor,
  // yani 30 saniyelik tazeleme yakınlaştırmayı sıfırlardı. Ölçüldü.
  const seriler = useMemo(() => durumlar.map((d) => ({
    name: d,
    data: bantlar
      .filter((b) => b.durum === d)
      .map((b) => ({
        x: d,
        y: [new Date(b.bas).getTime(), new Date(b.son).getTime()],
        // İpucu için taşınıyor; ApexCharts ek alanları koruyor.
        adet: b.adet,
        oran: b.oran,
      })),
  })), [durumlar, bantlar])

  const renkler = useMemo(() => durumlar.map((d, i) => renkSec(d, i)), [durumlar])

  const secenekler: ApexOptions = useMemo(() => ({
    chart: {
      type: 'rangeBar',
      height: Math.max(160, 52 + durumlar.length * 44),
      fontFamily: YAZI,
      toolbar: { show: false },
      animations: { enabled: false },
      background: 'transparent',
      ...YEREL,
      // Sürükleyerek aralık seçme. `toolbar.show:false` bunu ÖLDÜRMÜYOR —
      // öldüren `toolbar.tools.zoom:false` olurdu (Base.js: zoomEnabled =
      // autoSelected==='zoom' && tools.zoom && zoom.enabled; `show` bu
      // ifadede geçmiyor). Ölçüldü.
      zoom: {
        enabled: true,
        type: 'x',
        // `'auto'` toolbar'ın reset düğmesini şart koşuyor
        // (ZoomPanSelection: _incidentalZoomEnabled). Toolbar'ı gizlediğimiz
        // için AÇIKÇA true verilmek zorunda, yoksa tekerlek hiç çalışmaz.
        allowMouseWheelZoom: true,
      },
      events: {
        // Apex'in KENDİ reset'i kullanılmıyor: updateOptions
        // `overwriteInitialConfig` ile initialConfig'i tazeliyor, dolayısıyla
        // ilk yakınlaştırmadan sonra reset "hiçbir yere" dönüyordu. Sıfırlama
        // sayfanın state'inde.
        zoomed: (_c, o) => {
          const x = o?.xaxis
          if (x && typeof x.min === 'number' && typeof x.max === 'number') {
            zoomIste(x.min, x.max)
          }
        },
      },
    },
    colors: renkler,
    plotOptions: {
      bar: {
        horizontal: true,
        // Aynı satırdaki tüm bantlar tek şeride otursun ve şerit yüksekliğini
        // doldursun diye — bkz. dosya başı (1).
        rangeBarGroupRows: true,
        barHeight: '62%',
      },
    },
    // Kova modunda bant, kovanın ne kadarını kapladığını saydamlıkla anlatıyor.
    fill: kovaMi
      ? {
          type: 'solid',
          opacity: bantlar.length > 0 ? undefined : 1,
        }
      : { type: 'solid', opacity: 1 },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '11px',
      itemMargin: { horizontal: 8 },
      // Tek durumlu etikette de görünsün — bkz. dosya başı (2).
      showForSingleSeries: true,
      markers: { size: 6 },
    },
    grid: {
      borderColor: RENK.izgara,
      strokeDashArray: 3,
      padding: { left: 4, right: 8 },
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    xaxis: {
      type: 'datetime',
      // SAYI olmak zorunda — bkz. dosya başı (4). Pencere kullanıcının
      // seçtiği aralık: veri dar bir yere sıkışsa bile sessiz kalan kısım
      // ekranda BOŞ görünmeli.
      min: new Date(cizelge.bas).getTime(),
      max: new Date(cizelge.son).getTime(),
      labels: {
        style: { colors: RENK.govde, fontSize: '10px', fontFamily: MONO },
        datetimeUTC: false,
        // formatter YOK — bkz. dosya başı (3).
      },
      axisBorder: { color: RENK.izgara },
      axisTicks: { color: RENK.izgara },
    },
    yaxis: {
      labels: {
        style: { colors: RENK.govde, fontSize: '10.5px', fontFamily: MONO },
        maxWidth: 150,
      },
    },
    tooltip: {
      // Nokta KENDİ dizimizden okunuyor, `w.config`ten değil: ApexCharts'ın
      // seri tipi gevşek (number | {…}) ve oradan okumak her alanda cast
      // gerektirirdi. İndeksler aynı — seriyi biz üretiyoruz.
      custom: ({ seriesIndex, dataPointIndex }) => {
        const nokta = seriler[seriesIndex]?.data?.[dataPointIndex]
        if (!nokta) return ''
        const durum = seriler[seriesIndex].name
        const [bas, son] = nokta.y
        const sn = Math.max(0, (son - bas) / 1000)
        const oran = nokta.oran
        return (
          `<div style="padding:6px 9px;font-family:${YAZI};font-size:11px;line-height:1.6">` +
          `<b>${kacir(durum)}</b><br/>` +
          `${kacir(trTarihSaat(new Date(bas).toISOString()))} → ` +
          `${kacir(trTarihSaat(new Date(son).toISOString()))}<br/>` +
          `<span style="color:${RENK.govde}">süre ${kacir(sure(sn))}` +
          (kovaMi ? ` · kovanın %${kacir(tr(oran * 100, 0))}'i` : '') +
          `</span></div>`
        )
      },
    },
    noData: {
      text: 'Bu aralıkta durum okuması yok',
      style: { color: RENK.govde, fontSize: '12px', fontFamily: YAZI },
    },
  }), [cizelge.bas, cizelge.son, renkler, seriler, durumlar.length, kovaMi, bantlar.length, zoomIste])

  return (
    <div style={{ padding: '4px 4px 0' }}>
      {/* Sıfırlama düğmesi Apex'in toolbar'ı DEĞİL: kütüphanenin reset'i
          `initialConfig.xaxis`'e dönüyor ve updateOptions o yapılandırmayı
          tazelediği için ilk yakınlaştırmadan sonra "hiçbir yere" dönüyordu.
          Düğme yalnız yakınlaştırma varken çıkıyor — boşken yer kaplamasın. */}
      {zoomVar && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0 8px 6px', fontSize: 11,
          }}
        >
          <span className="cell-sub">
            Yakınlaştırılmış görünüm — veriler bu aralık için yeniden hesaplandı.
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '2px 10px', fontSize: 11, minHeight: 0 }}
            onClick={zoomSifirla}
          >
            Yakınlaştırmayı sıfırla
          </button>
        </div>
      )}
      <Suspense
        fallback={
          <div className="cell-sub" style={{ padding: 24, textAlign: 'center' }}>
            Grafik yükleniyor…
          </div>
        }
      >
        <Chart
          options={secenekler}
          series={seriler}
          type="rangeBar"
          height={Math.max(160, 52 + durumlar.length * 44)}
        />
      </Suspense>

      <div className="cell-sub" style={{ fontSize: 10.5, padding: '0 8px 8px' }}>
        {karne.map((k) => `${k.durum} · ${tr(k.bantSayisi, 0)} kez · ${sure(k.toplamSn)}`)
          .join(' — ')}
        {karne.length > 0 && <br />}
        {kovaMi ? (
          <>
            <b>Kovalanmış görünüm:</b> {tr(cizelge.hamBantSayisi, 0)} gerçek geçiş bu
            pencereye sığmadığı için her bant {sure(cizelge.kovaSn ?? 0)}'lık bir kovanın{' '}
            <b>baskın</b> durumunu gösteriyor — tek tek geçişler için daha kısa bir
            aralık seçin.
          </>
        ) : (
          <>
            {tr(bantlar.length, 0)} bant · ardışık aynı okumalar birleştirildi ·{' '}
            {sure(cizelge.boslukEsigiSn)}'den uzun veri boşluğunda bant kesiliyor
          </>
        )}
        <br />
        Grafik üzerinde sürükleyerek ya da tekerlekle yakınlaştırabilirsiniz;
        yakınlaşınca veriler o aralık için yeniden hesaplanır.
      </div>
    </div>
  )
}
